import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, SlidersHorizontal, Users } from "lucide-react";

import { api, photoUri } from "@/lib/api";
import { membershipLabel } from "@/lib/format";
import { Modal } from "@/components/modal";
import { AdminPage, ChipRow, Gate, Meta, Pager, statusTone } from "@/components/admin-ui";
import { Button, ChipGroup, EmptyState, ErrorState, Field, Loading, Pill, SectionLabel } from "@/components/ui";

const STATUSES = [
  { id: "", label: "All" }, { id: "active", label: "Active" }, { id: "restricted", label: "Restricted" },
  { id: "suspended", label: "Suspended" }, { id: "deactivated", label: "Deactivated" }, { id: "pending_verification", label: "Email pending" },
];
const EMPTY = { reg_from: "", reg_to: "", mission: [], country: "", city: "", verification: [], test: [] };

export default function AdminMembers() {
  return <Gate perm="members" testId="admin-members-forbidden"><Inner /></Gate>;
}

function Inner() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [filters, setFilters] = useState(EMPTY);
  const [draft, setDraft] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const p = new URLSearchParams();
  if (q.trim()) p.set("q", q.trim());
  if (status) p.set("status", status);
  if (filters.reg_from) p.set("reg_from", filters.reg_from);
  if (filters.reg_to) p.set("reg_to", filters.reg_to);
  if (filters.mission[0]) p.set("mission", filters.mission[0].toLowerCase());
  if (filters.country.trim()) p.set("country", filters.country.trim());
  if (filters.city.trim()) p.set("city", filters.city.trim());
  if (filters.verification[0]) p.set("verification", filters.verification[0].toLowerCase());
  if (filters.test[0]) p.set("test", filters.test[0] === "Test profiles" ? "true" : "false");
  p.set("page", String(page));
  const qs = p.toString();
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["admin-members", qs], queryFn: () => api(`/admin/members?${qs}`), placeholderData: (prev) => prev });
  const active = Object.values(filters).filter((v) => (Array.isArray(v) ? v.length : v)).length;
  const d = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });

  return (
    <AdminPage title="Members" testId="admin-members-screen">
      <div className="flex gap-2">
        <label htmlFor="member-search" className="sr-only">Search members</label>
        <input id="member-search" data-testid="admin-members-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name or email"
          className="h-11 flex-1 rounded-full border border-border bg-white px-4 text-sm focus:border-primary focus:outline-none" />
        <Button testId="admin-members-filters-button" title={active ? `Filters (${active})` : "Filters"} icon={SlidersHorizontal} size="sm" variant="outline" onClick={() => { setDraft(filters); setOpen(true); }} />
      </div>
      <ChipRow options={STATUSES} value={status} onChange={(v) => { setStatus(v); setPage(1); }} testIdPrefix="admin-members-status" />
      {isLoading ? <Loading /> : isError ? <ErrorState testId="admin-members-error" message={error.message} onRetry={() => refetch()} /> : (
        <>
          <Meta testId="admin-members-total">{data.total ?? 0} members</Meta>
          {!data.members.length ? <EmptyState testId="admin-members-empty" icon={Users} title="No members match" /> : (
            <div className="overflow-hidden rounded-[20px] border border-border bg-white">
              <table className="w-full text-left text-sm">
                <thead className="hidden bg-[#F4EBE6] text-xs uppercase tracking-wide text-muted-foreground md:table-header-group">
                  <tr><th className="p-3">Member</th><th className="p-3">Location</th><th className="p-3">Status</th><th className="p-3">Membership</th></tr>
                </thead>
                <tbody>
                  {data.members.map((m) => (
                    <tr key={m.id} data-testid={`admin-member-row-${m.id}`} tabIndex={0} onClick={() => navigate(`/admin/member/${m.id}`)} onKeyDown={(e) => e.key === "Enter" && navigate(`/admin/member/${m.id}`)}
                      className="flex cursor-pointer flex-col border-b border-border p-3 last:border-b-0 hover:bg-[#FDFBF7] focus:bg-[#F6E3E4] focus:outline-none md:table-row md:p-0">
                      <td className="md:p-3">
                        <div className="flex items-center gap-3">
                          <img src={photoUri(m.main_photo_url)} alt="" className="h-10 w-10 rounded-full bg-[#F4EBE6] object-cover" />
                          <div className="min-w-0"><p className="truncate font-bold">{m.full_name} <span className="font-normal text-muted-foreground">({m.display_name})</span></p><p className="truncate text-xs text-muted-foreground">{m.email}</p></div>
                        </div>
                      </td>
                      <td className="text-xs text-muted-foreground md:p-3 md:text-sm">{m.city}, {m.country}</td>
                      <td className="md:p-3">
                        <div className="mt-1 flex flex-wrap gap-1 md:mt-0">
                          <Pill label={m.account_status.replace("_", " ")} tone={statusTone(m.account_status)} />
                          {m.mission ? <Pill label={m.mission} tone="rose" /> : null}
                          {m.verification_status === "verified" ? <Pill label="Verified" tone="gold" icon={CheckCircle2} /> : null}
                          {m.is_test_profile ? <Pill label="Test profile" /> : null}
                        </div>
                      </td>
                      <td className="mt-1 md:mt-0 md:p-3">{m.membership ? <Pill testId={`admin-member-membership-${m.id}`} label={membershipLabel(m.membership)} tone={m.membership.locked ? "error" : m.membership.status === "active" ? "success" : "neutral"} /> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager testId="admin-members-pager" page={data.page} pages={data.pages} total={data.total} onChange={setPage} />
        </>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Filter members" testId="admin-members-filter-sheet">
        <SectionLabel>Registration date (YYYY-MM-DD)</SectionLabel>
        <div className="grid grid-cols-2 gap-3">
          <Field testId="admin-filter-reg-from" label="From" type="date" value={draft.reg_from} onChange={d("reg_from")} />
          <Field testId="admin-filter-reg-to" label="To" type="date" value={draft.reg_to} onChange={d("reg_to")} />
        </div>
        <SectionLabel>Mission</SectionLabel>
        <ChipGroup testIdPrefix="admin-filter-mission" multi={false} options={["Marriage", "Dating", "Flirtation"]} value={draft.mission} onChange={(v) => setDraft({ ...draft, mission: v })} />
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Field testId="admin-filter-country" label="Country" value={draft.country} onChange={d("country")} placeholder="Nigeria" />
          <Field testId="admin-filter-city" label="City" value={draft.city} onChange={d("city")} placeholder="Lagos" />
        </div>
        <SectionLabel>Verification</SectionLabel>
        <ChipGroup testIdPrefix="admin-filter-verification" multi={false} options={["Verified", "Unverified"]} value={draft.verification} onChange={(v) => setDraft({ ...draft, verification: v })} />
        <div className="mt-4"><SectionLabel>Profile type</SectionLabel></div>
        <ChipGroup testIdPrefix="admin-filter-test" multi={false} options={["Test profiles", "Real members"]} value={draft.test} onChange={(v) => setDraft({ ...draft, test: v })} />
        <div className="mt-6 flex gap-3">
          <Button testId="admin-filter-clear" title="Clear" variant="outline" className="flex-1" onClick={() => { setFilters(EMPTY); setPage(1); setOpen(false); }} />
          <Button testId="admin-filter-apply" title="Apply" className="flex-1" onClick={() => { setFilters(draft); setPage(1); setOpen(false); }} />
        </div>
      </Modal>
    </AdminPage>
  );
}
